// Centralised wrapper around all Capacitor plugins. Safe to import on web —
// every call no-ops or falls back to a web equivalent when not running inside
// the native shell.

import { Capacitor } from '@capacitor/core';

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();

/* ---------- Camera ---------- */
export async function takeNativePhoto() {
  if (!isNative()) return null;
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  const photo = await Camera.getPhoto({
    quality: 88,
    allowEditing: false,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Prompt,
  });
  return photo.dataUrl ?? null;
}

/** Convert a data-URL (e.g. from Capacitor Camera) into a File. */
export function dataUrlToFile(dataUrl, filename = `photo-${Date.now()}.jpg`) {
  if (!dataUrl?.startsWith('data:')) return null;
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/data:([^;]+);base64/);
  const mime = mimeMatch?.[1] || 'image/jpeg';
  const bin = atob(base64);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
}

/* ---------- Status bar ---------- */
/**
 * Tint the native Android status bar to match the current theme.
 *  - light theme → brand teal (#006666) with light (white) foreground icons
 *  - dark theme  → near-black (#0a0a0a) with light foreground icons
 * No-op on web / iOS notch. Errors are swallowed so it can never break the app.
 */
export async function syncStatusBar(theme) {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const isDark = theme === 'dark';
    await StatusBar.setBackgroundColor({ color: isDark ? '#0a0a0a' : '#006666' });
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
    // Ensure the WebView isn't painted under the status bar (Android quirk).
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch {
    /* plugin unavailable in web preview — ignore */
  }
}

/* ---------- Local notifications ---------- */
// Deterministic 31-bit integer id for each reminder (LocalNotifications API
// requires signed 32-bit ints and uses the id to overwrite duplicates).
function _reminderNotifId(reminderId) {
  let h = 0;
  for (let i = 0; i < reminderId.length; i++) {
    h = ((h << 5) - h + reminderId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) & 0x7fffffff;
}

// Convert a routine reminder → Capacitor LocalNotification schedule.
// Supports cadence 'daily' | 'weekly' | 'monthly'. time_of_day is 'HH:MM'.
function _scheduleFromReminder(rem) {
  const [hStr, mStr] = String(rem.time_of_day || '08:00').split(':');
  const hour = Math.max(0, Math.min(23, parseInt(hStr, 10) || 8));
  const minute = Math.max(0, Math.min(59, parseInt(mStr, 10) || 0));
  const on = {};
  if (rem.cadence === 'daily') {
    on.hour = hour;
    on.minute = minute;
  } else if (rem.cadence === 'weekly') {
    // fire on the same weekday the reminder was created
    on.hour = hour;
    on.minute = minute;
    on.weekday = 2; // Monday — a sensible weekly default for new habits
  } else if (rem.cadence === 'monthly') {
    on.hour = hour;
    on.minute = minute;
    on.day = 1;
  }
  return { every: rem.cadence || 'daily', on, allowWhileIdle: true };
}

/**
 * Reconcile the local device's scheduled notifications with the user's
 * enabled beauty reminders. Cancels stale notifications and (re)schedules
 * the current list. No-op on web.
 *
 * Only touches notification IDs that belong to beauty reminders — leaves
 * calendar-scheduled notifications (see syncCalendarNotifications) alone.
 */
export async function syncBeautyReminders(reminders = []) {
  if (!isNative()) return { scheduled: 0 };
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    // Ask for permission if we haven't got it yet.
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== 'granted') return { scheduled: 0, permissionDenied: true };
    }
    const enabled = (reminders || []).filter((r) => r?.id && r?.enabled !== false);
    const wantedIds = new Set(enabled.map((r) => _reminderNotifId(r.id)));
    // Cancel prior beauty notifications only — keep anything else (calendar).
    const pending = await LocalNotifications.getPending();
    const toCancel = (pending.notifications || []).filter((n) => wantedIds.has(n.id));
    if (toCancel.length) {
      await LocalNotifications.cancel({ notifications: toCancel.map((n) => ({ id: n.id })) });
    }
    if (enabled.length === 0) return { scheduled: 0 };
    await LocalNotifications.schedule({
      notifications: enabled.map((r) => ({
        id: _reminderNotifId(r.id),
        title: r.title || 'Time for your routine',
        body: r.note || `${r.type || 'Beauty'} · ${r.cadence || 'daily'}`,
        schedule: _scheduleFromReminder(r),
        smallIcon: 'ic_stat_icon',
      })),
    });
    return { scheduled: enabled.length };
  } catch (e) {
    return { error: String(e?.message || e) };
  }
}

/**
 * Schedule LocalNotifications for calendar outfit events and custom
 * reminders so the user gets a phone buzz at the event time. Handles both
 * one-time events (schedule at a single `at`) and recurring ones
 * (daily/weekly/monthly). Silently no-ops on web.
 *
 * We namespace calendar notifications by prefixing the reminder id with
 * "cal:" before hashing so they can coexist with beauty reminders without
 * id collisions.
 */
export async function syncCalendarNotifications(events = []) {
  if (!isNative()) return { scheduled: 0 };
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      const req = await LocalNotifications.requestPermissions();
      if (req.display !== 'granted') return { scheduled: 0, permissionDenied: true };
    }

    // Only touch our own calendar notifications — leave beauty ones alone.
    const wantedIds = new Set(
      (events || [])
        .filter((e) => e?.id && e.event_at)
        .map((e) => _reminderNotifId(`cal:${e.id}`)),
    );
    const pending = await LocalNotifications.getPending();
    const toCancel = (pending.notifications || []).filter((n) => wantedIds.has(n.id));
    if (toCancel.length) {
      await LocalNotifications.cancel({ notifications: toCancel.map((n) => ({ id: n.id })) });
    }

    const now = Date.now();
    const schedules = [];
    for (const e of events || []) {
      if (!e?.id || !e.event_at) continue;
      const dt = new Date(e.event_at);
      if (isNaN(dt.getTime())) continue;
      const recurrence = e.recurrence || 'one_time';
      // Skip past one-time events.
      if (recurrence === 'one_time' && dt.getTime() < now) continue;

      const base = {
        id: _reminderNotifId(`cal:${e.id}`),
        title: e.title || (e.kind === 'reminder' ? 'Reminder' : 'Upcoming event'),
        body: e.notes || (e.kind === 'reminder'
          ? 'You asked us to buzz you now.'
          : `${e.occasion || 'Event'}${e.location ? ' · ' + e.location : ''}`),
        smallIcon: 'ic_stat_icon',
      };

      if (recurrence === 'one_time') {
        schedules.push({ ...base, schedule: { at: dt, allowWhileIdle: true } });
      } else {
        const on = { hour: dt.getHours(), minute: dt.getMinutes() };
        if (recurrence === 'weekly') on.weekday = dt.getDay() + 1; // Capacitor: 1=Sun..7=Sat
        if (recurrence === 'monthly') on.day = dt.getDate();
        schedules.push({ ...base, schedule: { every: recurrence, on, allowWhileIdle: true } });
      }
    }

    if (schedules.length === 0) return { scheduled: 0 };
    await LocalNotifications.schedule({ notifications: schedules });
    return { scheduled: schedules.length };
  } catch (e) {
    return { error: String(e?.message || e) };
  }
}

/* ---------- Preferences (key-value storage) ---------- */
export async function kvSet(key, value) {
  if (!isNative()) { localStorage.setItem(key, value); return; }
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.set({ key, value });
}
export async function kvGet(key) {
  if (!isNative()) return localStorage.getItem(key);
  const { Preferences } = await import('@capacitor/preferences');
  const r = await Preferences.get({ key });
  return r.value ?? null;
}
export async function kvDelete(key) {
  if (!isNative()) { localStorage.removeItem(key); return; }
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.remove({ key });
}

/* ---------- Push Notifications ---------- */
export async function registerPushAndGetToken() {
  if (!isNative()) return null;
  const { PushNotifications } = await import('@capacitor/push-notifications');
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt') perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return null;
  await PushNotifications.register();
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(null), 5000);
    PushNotifications.addListener('registration', (token) => {
      clearTimeout(t);
      resolve(token.value);
    });
  });
}

/* ---------- Biometric (Face ID / Fingerprint) ---------- */
export async function biometricAuthenticate(reason = 'Sign in to Stylebay') {
  if (!isNative()) return false;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const avail = await NativeBiometric.isAvailable();
    if (!avail.isAvailable) return false;
    await NativeBiometric.verifyIdentity({
      reason,
      title: 'Stylebay',
      subtitle: 'Authenticate to continue',
    });
    return true;
  } catch {
    return false;
  }
}

/* ---------- Google Sign-In (native) ---------- */
// Web (server) OAuth client id — must match the string_resource `server_client_id`
// in android/app/src/main/res/values/strings.xml AND the GoogleAuth.serverClientId
// in capacitor.config.ts. If any of these drift, Sign-In fails with
// DEVELOPER_ERROR / code 10.
const GOOGLE_SERVER_CLIENT_ID =
  '270769125499-b23qjie61iuiorca4q50mkrm454hhuuf.apps.googleusercontent.com';

export async function nativeGoogleSignIn() {
  if (!isNative()) return null;
  const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
  // Explicitly pass the client id so we don't depend on strings.xml resolution.
  // Belt + suspenders — one of them is enough, both make debugging easier.
  try {
    await GoogleAuth.initialize({
      clientId: GOOGLE_SERVER_CLIENT_ID,
      scopes: ['profile', 'email'],
      grantOfflineAccess: false,
    });
  } catch {
    /* already initialised — ignore */
  }
  const user = await GoogleAuth.signIn();
  if (!user?.authentication?.idToken || !user.email) return null;
  return { idToken: user.authentication.idToken, email: user.email };
}

/**
 * Native Google sign-out — clears the cached Google account inside the
 * plugin so the NEXT `signIn()` re-prompts the account picker instead of
 * silently returning the last-used account. Called from AuthProvider.logout().
 */
export async function nativeGoogleSignOut() {
  if (!isNative()) return;
  try {
    const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
    // Ensure the SDK is initialised (idempotent) — signOut on an
    // uninitialised instance throws in v3.4.
    try {
      await GoogleAuth.initialize({
        clientId: GOOGLE_SERVER_CLIENT_ID,
        scopes: ['profile', 'email'],
        grantOfflineAccess: false,
      });
    } catch { /* already initialised */ }
    await GoogleAuth.signOut();
  } catch (e) {
    // Non-fatal — sign-out best-effort. We still clear the app session below.
    // eslint-disable-next-line no-console
    console.warn('[GoogleAuth] signOut failed:', e?.message || e);
  }
}

/* ---------- Apple Sign-In ---------- */
export async function nativeAppleSignIn() {
  if (!isNative() || platform() !== 'ios') return null;
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
  const res = await SignInWithApple.authorize({
    clientId: 'com.stylebay.app',
    redirectURI: 'https://smart-wardrobe-ai-6.emergent.host/auth/apple/callback',
    scopes: 'email name',
    state: 'stylio',
  });
  if (!res.response?.identityToken) return null;
  return {
    idToken: res.response.identityToken,
    email: res.response.email ?? undefined,
  };
}

/* ---------- Deep Links ---------- */
export async function listenForDeepLinks(handler) {
  if (!isNative()) return () => {};
  const { App } = await import('@capacitor/app');
  const sub = await App.addListener('appUrlOpen', (event) => {
    try {
      const url = new URL(event.url);
      const path = url.pathname + url.search + url.hash;
      handler(path || '/');
    } catch {
      const stripped = event.url.replace(/^[a-z]+:\/\//i, '/');
      handler(stripped);
    }
  });
  return () => sub.remove();
}

/* ---------- App resume (used for App Open ads) ---------- */
export async function listenForAppResume(handler) {
  if (!isNative()) return () => {};
  const { App } = await import('@capacitor/app');
  const sub = await App.addListener('appStateChange', ({ isActive }) => {
    // Fires when app comes back to foreground.
    if (isActive) handler();
  });
  return () => sub.remove();
}
